import $ from 'jquery';
import _ from 'underscore';
import { track } from 'util/analytics';
import * as flags from 'util/flags';
import {
  $body,
  $document,
  $window,
  jqCreateElem,
  jqGetElemById,
  isMobile,
  jqOffOn,
  setScrollPosition,
  userIsAuthenticated,
} from 'util/global';
import { onDOMReady, onLoad } from 'util/onload';
import { getParamsObject } from 'util/urls';
import { appendGetParam, appendGetParams } from './urls';

/** @namespace */
const UI = {};

/**
 * Custom selectboxes.
 * @class
 */
UI.CustomSelectbox = (function () {
  /**
   * @constructor
   * @param {JQuery} $element
   */
  function CustomSelectbox($element) {
    var $select = $('select', $element),
      $trigger = $('.dropdown-toggle', $element),
      $options = $('.dropdown-menu a', $element),
      active_class = 'active',
      active_query = '.' + active_class;

    $options.on('click', function (e) {
      var $option = $(this);
      e.preventDefault();
      if ($option.hasClass('disabled')) {
        return;
      }
      $trigger.find('span:first').html($option.html());
      $select.val($option.data('opt-value'));
      $select.trigger('change');
      $options.filter(active_query).removeClass(active_class);
      $option.addClass(active_class);
    });

    // if select loads with selected option, make sure the dropdown reflects that
    $options.filter(active_query).trigger('click');
  }

  return CustomSelectbox;
})();

/**
 * Make native anchors ("#...") work properly with the fixed header and modals
 * NOTE: added an anchor class with padding-top=header_height and margin-top=-header_height
 * so no need to call scrollToTarget for class=anchor on init
 * TODO: refactor this
 * KELLY 11/6/2014
 * @module
 */
UI.Anchors = new (function () {
  var self = this,
    CONFIG = {
      // may be a modal or just some scrollable div
      scrollable_container_selector: '.nf-feeditem-modal',
    };

  /**
   * Scrolls the document to the target element
   * @public
   * @param {JQuery} $target Element to scroll to
   * @param {function} [callback]
   */
  self.scrollToTarget = function ($target, callback) {
    // default arguments
    callback = callback || function () {};

    var $scrollable = $target.parents(CONFIG.scrollable_container_selector).first();
    if ($scrollable.length && $scrollable[0].scrollHeight == $scrollable.outerHeight()) {
      // not actually scrollable
      $scrollable = $();
    }

    if ($target.length) {
      const targetTop = $target.offset().top;
      /** @type {JQuery} */
      let $fixedElements;
      let offset = 0;

      if (!$scrollable.length) {
        if (isMobile) {
          $fixedElements = $('.m-action-bar');
        } else if (userIsAuthenticated) {
          $fixedElements = jqGetElemById('header');
        }
      }

      // calculate total offset
      if (!$target.hasClass('anchor') && $fixedElements) {
        $fixedElements.each((index, elem) => {
          offset += $(elem).outerHeight();
        });
      }

      if ($scrollable.length) {
        // scroll the container
        const targetPosition = targetTop - $scrollable.offset().top;
        setScrollPosition(targetPosition + $scrollable.scrollTop() - offset, {
          $container: $scrollable,
          animate: true,
          callback,
        });
      } else {
        // just scroll the document
        setScrollPosition(targetTop - offset, {
          animate: true,
          callback,
        });
      }
    } else {
      callback();
    }
  };

  /**
   * Scroll to a specified target when clicking an element
   * @param {JQuery} $element
   */
  self.controller = $element => {
    jqOffOn($element, 'click.scrolltotarget', e => {
      e.preventDefault();
      self.scrollToTarget($($element.data('target')));
    });
  };

  /** @public */
  self.init = function () {
    const { hash } = window.location;
    const $target = jqGetElemById(hash.substr(1));
    if (hash && hash.length && $target.length > 0 && !$target.hasClass('anchor')) {
      setScrollPosition(0);
      // used setTimeout here to scroll window right AFTER it is scrolled natively
      setTimeout(() => {
        setScrollPosition(0); // again, for browser compatibility
        self.scrollToTarget($target);
      }, 10);
    }

    $document.on('click', 'a[href^="#"]', e => {
      e.preventDefault();
      const thisHash = $(e.currentTarget).attr('href').substr(1);
      if (thisHash && thisHash.length) {
        const $hashElement = jqGetElemById(thisHash);
        if ($hashElement.length) {
          self.scrollToTarget($hashElement);
          window.location.hash = thisHash;
        }
      }
    });
  };

  return self;
})();

/** Scrolls position:fixed elements horizontally if needed */
UI.scrollFixedElements = () => {
  let elements = [
    {
      $elem: jqGetElemById('header'),
      shiftProperty: 'margin-left',
    },
  ];
  let scrollLeftOld = 0;
  let active = false;

  elements = elements.filter(e => e.$elem.length > 0);
  if (elements.length === 0) {
    return;
  }

  const scrollElements = () => {
    const scrollLeft = $window.scrollLeft();

    if (scrollLeft !== scrollLeftOld) {
      elements.forEach(elem => {
        elem.offset = elem.offset || parseInt(elem.$elem.css(elem.shiftProperty), 10);
        const newOffset = elem.offset - (scrollLeft - scrollLeftOld);
        elem.$elem.css(elem.shiftProperty, `${newOffset}px`);
        elem.offset = newOffset;
      });
      scrollLeftOld = scrollLeft;
    }
  };

  const checkState = () => {
    const { scrollWidth, clientWidth } = document.documentElement;

    if (!active && scrollWidth > clientWidth) {
      scrollElements();
      $window.on('scroll', scrollElements);
      active = true;
    }

    if (active && scrollWidth === clientWidth) {
      // remove the override from "style" attribute
      elements.forEach(e => e.$elem.css(e.shiftProperty, ''));
      $window.off('scroll', scrollElements);
      active = false;
    }
  };

  checkState();
  $window.on('resize', checkState);
};

/**
 * Crop the page. Used for overlaying fixed position elements.
 * @param {boolean} [shouldCrop] - set to false to undo cropping.
 */
UI.crop = function (shouldCrop) {
  _.defer(() => {
    // desktop and android
    if (!isMobile || navigator.userAgent.toLowerCase().indexOf('android') >= 0) {
      $body.toggleClass('cropped', shouldCrop !== false);

      // iOS
    } else {
      const $target = $body.children('.modal-bg');
      if (shouldCrop !== false) {
        // see http://stackoverflow.com/questions/16889447/prevent-full-page-scrolling-ios
        $target
          .on('touchstart.crop', function (e) {
            this.allowUp = this.scrollTop > 0;
            this.allowDown = this.scrollTop < this.scrollHeight - this.clientHeight;
            this.lastY = e.originalEvent.pageY;
          })
          .on('touchmove.crop', function (e) {
            var event = e.originalEvent;
            var up = event.pageY > this.lastY;
            var down = !up;
            this.lastY = event.pageY;

            if ((up && this.allowUp) || (down && this.allowDown)) {
              event.stopPropagation();
            } else {
              event.preventDefault();
            }
          });
      } else {
        $target.off('.crop');
      }
    }
  });
};

/**
 * Creates a "sticky" element
 * @param {JQuery} $container
 */
UI.sticky = function ($container) {
  $container.affix({
    offset: {
      top: $container.offset().top + ($container.data('offset-top') || 0),
    },
  });
};

/**
 * Switches the "loading" state of a button. In this state, the button
 * will have an animated "Loading..." copy
 */
UI.toggleButtonLoadingState = (function () {
  var interval = null;

  /**
   * @param {JQuery} $element Element (likely the button element), the copy of which should change
   * @param {boolean} is_loading True, if the button is in the "loading" state
   */
  return function ($element, is_loading) {
    if (is_loading && !interval) {
      var $dot2 = $('<span/>').text('.').addClass('dot').css('visibility', 'hidden'),
        $dot3 = $dot2.clone(),
        $logo = $element.find('.b2-flag').clone(),
        num_visible_dots = 1;
      $element
        .data('original-text', $element.text())
        .text('Loading.')
        .append($dot2, $dot3)
        .append($logo);
      interval = setInterval(function () {
        num_visible_dots = num_visible_dots == 3 ? 1 : num_visible_dots + 1;
        $dot2.css('visibility', num_visible_dots > 1 ? 'visible' : 'hidden');
        $dot3.css('visibility', num_visible_dots == 3 ? 'visible' : 'hidden');
      }, 333);
    } else if (interval) {
      $element.html($element.data('original-text'));
      clearInterval(interval);
      interval = null;
    }
  };
})();

/**
 * Modal confirmation box.
 * @param {object} [args]
 *     @param {string} [args.title]
 *     @param {string} [args.text]
 *     @param {string} [args.cancelBtnText]
 *     @param {string} [args.confirmBtnText]
 *     @param {string} [args.classes]
 * @returns {Promise}
 */
UI.showConfirmationModal = ({
  title = 'Are you sure?',
  text,
  cancelBtnText = 'Cancel',
  confirmBtnText = 'Yes',
  classes = '',
} = {}) =>
  new Promise(resolve => {
    let confirmed = false;
    const textHTML = text ? `<p>${text}</p>` : '';

    // remove an older instance of the  modal, if any
    jqGetElemById('confirm-modal').remove();

    let modalHTML = `<div class="modal hide ${
      !title ? 'no-title' : ''
    } ${classes}" id="confirm-modal">
            <div class="modal-header"><h3>${title}</h3></div>
            <div class="modal-body">${textHTML}</div>
            <div class="modal-footer">
                <a href="#" class="btn cancel" data-dismiss="modal">${cancelBtnText}</a>
                <a href="#" class="btn btn-accent confirm">${confirmBtnText}</a>
            </div>
        </div>`;

    modalHTML = modalHTML.replace(/>\s+</g, '><');

    $body.append(modalHTML);

    const $modal = jqGetElemById('confirm-modal');

    $modal.find('.confirm').on('click', e => {
      e.preventDefault();
      confirmed = true;
      $modal.modal('hide');
    });

    $modal.on('hide', () => {
      resolve(confirmed);
    });

    $('body #confirm-modal').modal();
  });

/**
 * @controller
 * Requests a confirmation when you click on the element.
 * Make sure you specified a "data-confirm-box-title" attribute.
 * @param {JQuery} $element
 */
UI.areYouSure = $element => {
  let confirmedBefore = false;

  $element.on('click', e => {
    if (!confirmedBefore) {
      e.preventDefault();
      UI.showConfirmationModal({
        title: $element.data('confirm-box-title'),
      }).then(confirmed => {
        if (confirmed) {
          confirmedBefore = true;
          $element.trigger('click');
        }
      });
    }
  });
};

/**
 * @typedef ModalControllerData
 * @property {String} flag A profile flag to set when viewing the modal
 * @property {String} axId Analytics element_id to trigger when viewing the modal
 * @property {String} trigger Query for an element to click before showing the modal
 * @property {Boolean} autoshow Show the modal automatically when a trigger is defined
 */

/**
 * Generic modal controller, automatically opens the modal unless a trigger is specified
 * @param {JQuery} $modal
 */
UI.modalController = function ($modal) {
  /** @type {ModalControllerData} */
  const data = $modal.data();

  $modal.on('show', function () {
    // track open if data-ax-id
    if (data.axId) {
      // for autoshow modals, this might fire before analytics functions are ready
      setTimeout(() => {
        track('ui', {
          action: 'modal',
          element_type: 'modal',
          element_id: data.axId,
        });
      }, 1);
    }

    // set the flag if one exists
    if (data.flag) {
      flags.update(data.flag, true);
    }
  });

  $modal.modal({
    show: false,
  });

  // show modal automatically if data-trigger isn't given
  let showModal = true;

  if (data.trigger) {
    $(data.trigger).on('click', e => {
      e.preventDefault();
      $modal.modal('show');
    });

    showModal = data.autoshow === true;
  }

  if (showModal) {
    $modal.modal('show');
  }
};

/**
 * Controller for tall dropdowns that should be fitted into the viewport by reducing their
 * height and adding a scrollbar to them
 * @param {JQuery} $dropdown
 */
UI.fitDropdownIntoViewport = function ($dropdown) {
  var $dropdownMenu = $dropdown.find('.dropdown-menu'),
    margin = 10, // desirable margin between the dropdown and the bottom of the page (px)
    windowEvents = 'resize.ddresize scroll.ddresize',
    scrollClass = 'vertical-scrollbar',
    fitted = false,
    initialHeight;

  /** Checks that the dropdown doesn't exceed the bottom of the page */
  function resizeDropdown() {
    const maxY = $window.height() + $document.scrollTop() - margin;
    const y = $dropdownMenu.offset().top + $dropdownMenu.outerHeight();
    const newHeight = $dropdownMenu.height() - (y - maxY);

    if (newHeight > initialHeight) {
      if (fitted) {
        $dropdownMenu.css('height', 'auto').removeClass(scrollClass);
        fitted = false;
      }
    } else {
      if (!fitted) {
        $dropdownMenu.addClass(scrollClass);
        fitted = true;
      }
      $dropdownMenu.height(newHeight);
    }
  }

  function recalculate() {
    initialHeight = $dropdownMenu.height();
    resizeDropdown();
  }

  $dropdown
    .on('opened', () => {
      recalculate();
      $window.on(windowEvents, _.throttle(resizeDropdown, 20));
    })
    .on('close', () => {
      $dropdownMenu.css('height', 'auto');
      $window.off(windowEvents);
    })
    .on('update', recalculate);
};

UI.fadeOutOnComponentLoad = function ($element) {
  $element.fadeOut(2000);
};

/**
 * Requests an autocomplete list from a URL and handles the selection of an item from that list.
 * Requires a data-autocomplete-url attribute on the main container as well as two children:
 * .autocomplete-field and .autocomplete-target. autocomplete-field is a visible text input for
 * searching for an item and the autocomplete-target is a hidden input to save the selected
 * item's id.
 * @param {JQuery} $container
 */
UI.autocomplete = function ($container) {
  var $field = $container.find('.autocomplete-field'),
    $target = $container.find('.autocomplete-target'),
    data = $container.data();

  if (data.autocompleteUrl && $field.length && $target.length) {
    var timeout,
      currentResponse = [];

    var getItemTitleFromData = function (itemData) {
      var title = itemData.title || itemData.name;
      if (data.showIds) {
        // "example [1234]"
        title += ' [' + itemData.id + ']';
      }
      return title;
    };

    var autocompleteEvent = function (eventName, data) {
      return $.Event('autocomplete.' + eventName, {
        autocomplete: data,
      });
    };

    $field.typeahead();

    $field.on('focus', function () {
      $field.select();
    });

    $field.on('keypress', function (e) {
      clearTimeout(timeout);
      // wait for the user to stop typing
      timeout = setTimeout(function () {
        var query = $field.val();
        // don't search until the user has typed a few chars
        if (query.length >= 3) {
          // WARNING! Use .data(...) here because the url might change between requests
          var url = $container.data('autocomplete-url');
          var params = {};
          params[data.paramName || 'query'] = query;
          $.get(url, params).then(function (response) {
            currentResponse = response;
            $field.data('typeahead').source = response.map(getItemTitleFromData);
            if (response.length) {
              $field.typeahead('lookup');
            }
          });
        }
      }, 500);
    });

    $field.on('ta_select', function (e) {
      var currentValue = $field.val(),
        item;
      for (var i in currentResponse) {
        item = currentResponse[i];
        if (getItemTitleFromData(item) === currentValue) {
          $target.val(item.id);
          $target.trigger('change');
          $field.trigger(autocompleteEvent('select', item));
          $field.data('previous-value', currentValue);
          return;
        }
      }
    });

    $field.on('blur', function () {
      setTimeout(function () {
        $field.typeahead('hide');

        // field was intentionally cleared
        if ($field.val() === '') {
          $target.val('');
          $target.trigger('change');
          $field.trigger(
            autocompleteEvent('select', {
              id: null,
              title: null,
            }),
          );

          // field was either selected or not cleared
        } else {
          $field.val($field.data('previous-value'));
        }
      }, 200);
    });

    $field.on('autocomplete.reset', function () {
      $field.data('typeahead').source = [];
    });

    $field.data('previous-value', $field.val());

    // defer to make sure that listeners are ready before we fire the event
    _.defer(function () {
      $field.trigger(
        autocompleteEvent('ready', {
          id: $target.val(),
          title: $field.val(),
        }),
      );
    });
  }
};

/**
 * Controller for text fields (inputs, textareas) with a placeholder
 * @param {JQuery} $field
 */
UI.hasPlaceholder = function ($field) {
  var placeholder = $field.data('placeholder');

  if (placeholder) {
    var placeholder_class = 'placeholder',
      setPlaceholder = function () {
        if ($field.val().trim() === '') {
          $field.val(placeholder).addClass(placeholder_class);
        }
      },
      removePlaceholder = function () {
        if ($field.hasClass(placeholder_class)) {
          $field.val('').removeClass(placeholder_class);
        }
      };

    if ($field.val() === placeholder) {
      $field.val('');
    }

    if (!$field.is(':focus') && !$field.is(':selected')) {
      setPlaceholder();
    }

    $field.on('focus select', removePlaceholder).on('blur', setPlaceholder);
    $field.parents('form').on('submit', removePlaceholder);
  }
};

/**
 * Show a warning on a field if the user enters too many \quad or \qquad statements
 * @param {JQuery} $container
 */
UI.quadLimitWarning = function ($container) {
  var $textarea = $container.find('textarea');
  var $warning = null;
  var warningIsVisible = false;
  var warningMsg = $container.data('quad-warning');
  var quadLimit = 5;
  var quadRegex = /\\qq?uad/g;

  var checkValue = function () {
    var matches = $textarea.val().match(quadRegex);
    if (matches && matches.length >= quadLimit) {
      warningIsVisible = true;
      if ($warning) {
        $warning.show();
      } else {
        $warning = $('<ul class="errorlist"><li>' + warningMsg + '</li></ul>');
        $container.append($warning);
      }
    } else if ($warning && warningIsVisible) {
      warningIsVisible = false;
      $warning.hide();
    }
  };

  $textarea.on('keyup input', checkValue);
  _.defer(checkValue);
};

/**
 * Show and hide hidden content
 * @param {JQuery} $btnContainer
 */
UI.showAndHideContent = function ($btnContainer) {
  $btnContainer.find('.btn').on('click', function (e) {
    e.preventDefault();
    $btnContainer.nextAll('blockquote.hidden').first().toggleClass('show');
  });
};

UI.preventDoubleClick = function ($container) {
  $container.find('a,button').on('click', function (e) {
    var $this = $(this);
    if ($this.hasClass('disabled')) {
      e.preventDefault();
    } else {
      $this.addClass('disabled');
    }
  });
};

UI.genericSignupModal = function ($btn) {
  const $modal = jqGetElemById('signup-modal-generic');
  if ($modal.length) {
    $modal.modal({ show: false });
    $btn.on('click', function (e) {
      e.preventDefault();
      $modal.modal('show');

      const isOneStep = $modal.data('one-step') === true;
      const isLoginButton = $btn.data('show-login');

      const $modalTourContainer = $modal.find('.public-signup-left');
      if ($modalTourContainer.length && !$modalTourContainer.data('already-opened')) {
        $modalTourContainer.data('already-opened', true);
        $modalTourContainer.attr('data-already-opened', true);

        track('ui', {
          action: 'viewed_prenux_slide',
          step_position: 1,
          is_first_step: true,
          is_last_step: false,
        });
      }

      var $modalFormContainer = $modal.find('#public-signup');
      if ($modalFormContainer.length) {
        var formClass = 'show-form';
        var signupClass = 'show-signup';
        var loginClass = 'show-login';
        if (isLoginButton) {
          $modalFormContainer.removeClass(formClass + ' ' + signupClass).addClass(loginClass);
        } else {
          $modalFormContainer.removeClass(formClass + ' ' + loginClass).addClass(signupClass);
        }
      }

      const nextUrl = $btn.data('next');
      if (nextUrl) {
        $modal.find('.btn-email').data('next', nextUrl);
        $modal.find('.signup-social').each((i, elem) => {
          const path = elem.href.split('?')[0];
          const query = `next=${encodeURIComponent(nextUrl)}`;
          $(elem).attr('href', `${path}?${query}`);
        });
      }

      const signupCourseSlug = getParamsObject()['from-course'];
      if (signupCourseSlug) {
        $modal.find('.btn-email').data('signup_course_slug', signupCourseSlug);
        $modal.find('.signup-social').each((i, elem) => {
          if (elem.href.indexOf('from-course=') < 0) {
            let [base, next] = elem.href.split('next=');
            next = decodeURIComponent(next);
            next = appendGetParam(next, 'from-course', signupCourseSlug);
            let [nextBase, nextParams] = next.split('?');
            nextParams = encodeURIComponent(`?${nextParams}`);
            const result = `${base}next=${nextBase}${nextParams}`;
            $(elem).attr('href', result);
          }
        });
      }

      if (isOneStep) {
        const query = isLoginButton ? '#problem-login-link' : '#signup-email';
        const $targetBtn = $modal.find(query);
        // You have to click the button to load the form :face_with_rolling_eyes:
        $targetBtn.trigger('click');
      }
    });
  }
};

UI.selectAll = function ($input) {
  var parentSelector = $input.data('parent') || null;
  var $element;
  if (parentSelector) {
    $element = $input.parents(parentSelector);
  } else {
    $element = $input;
  }
  $element.on('click', function (e) {
    $input.select();
  });
};

/**
 * Controller.
 * Adds a special class to the select in case the first option (placeholder) is selected.
 * @param {JQuery} $select
 */
UI.selectPlaceholder = $select => {
  if ($select[0].tagName !== 'SELECT') {
    return;
  }

  const NOT_SELECTED_CLASS = 'not-selected';

  const toggleClass = () => {
    $select.toggleClass(NOT_SELECTED_CLASS, $select[0].selectedIndex === 0);
  };

  toggleClass();
  $select.on('change', toggleClass);
};

/**
 * Add left/right arrow keyboard handlers
 * @param {object} opts
 * @param {function} [opts.guard] - return false if arrows are disabled
 * @param {function} [opts.left] - handler for left arrow
 * @param {function} [opts.right] - handler for right arrow
 */
UI.keyboardNavigation = function (opts) {
  var eventKey = opts.key || '';
  var guard = opts.guard || _.noop;
  var leftHandler = opts.left || _.noop;
  var rightHandler = opts.right || _.noop;
  var inputTags = ['input', 'textarea'];

  var isInputting = function () {
    var el = document.activeElement;

    if (inputTags.indexOf(el.tagName.toLowerCase()) >= 0 && (!el.type || el.type !== 'button')) {
      // codemirror textarea is blank
      return (el.value && el.value.length) || $(el).parents('.CodeMirror').length;
    }

    return false;
  };

  jqOffOn($body, `keydown.keynav${eventKey}`, e => {
    // disable arrow nav while focused on anything
    if (e.metaKey || isInputting() || guard() === false) {
      return;
    }
    if (e.which === 37) {
      e.preventDefault();
      leftHandler();
    } else if (e.which === 39) {
      e.preventDefault();
      rightHandler();
    }
  });
};

/**
 * Checks if $elem is visible within the viewport or in relation to the $container
 * This is a drawing of how we think it works:
 * https://docs.google.com/a/brilliant.org/drawings/d/1ExZjPQLE1w1ApgcMoapoPmu-rdX4WB_I2ssqLvhgY98
 * @param {JQuery} $elem
 * @param {JQuery} $container - DOM element that contains the component we are checking
 * @returns {boolean}
 */
UI.isVisibleInContainer = ($elem, $container) => {
  if (!$elem.is(':visible')) {
    return false;
  }

  // we need to calculate the top or base offset in case we are in a modal
  // which is in front of a page that has been scrolled
  const topOffset = $container[0] === window ? 0 : $container.offset().top;
  const scrollTop = $container.scrollTop();

  // coordinates of the container viewport
  const top = scrollTop;
  const bottom = top + $container.height();

  // coordinates of the element relative to the container
  const elemTop = ($container[0] === window ? 0 : scrollTop) + $elem.offset().top - topOffset;
  const elemBottom = elemTop + $elem.outerHeight();

  // Top of the element is above the viewport bottom and
  // bottom of the element is below top of the viewport.
  // Returns true even if element is only partially visible.
  return elemTop <= bottom && elemBottom >= top;
};

/**
 * @param {JQuery} $elem
 * @returns {boolean}
 */
UI.isInViewport = function isInViewport($elem) {
  return UI.isVisibleInContainer($elem, $window);
};

/**
 * Prevent loading an image until everything else has had a chance to load
 * @param {JQuery} $image
 */
UI.deferImage = $image => {
  const src = $image.data('src');
  if (src) {
    onLoad(() => {
      // Defer this to give anything else a chance to start loading first
      setTimeout(() => {
        $image.on('load', () => {
          $image.removeAttr('width');
          $image.removeAttr('height');
        });
        $image.prop('src', src);
      }, 1);
    });
  }
};

/**
 * @param {string} [title]
 * @param {string} [text]
 * @returns {JQuery}
 */
UI.showGenericModal = (title, text) => {
  let html = '';

  // header
  html += '<div class="modal-header">';
  html += '<button type="button" class="close" data-dismiss="modal">&times;</button>';
  if (title) {
    html += `<h3>${title}</h3>`;
  }
  html += '</div>';

  // content
  html += '<div class="modal-body">';
  if (text) {
    html += `<p>${text}</p>`;
  }
  html += '</div>';

  // footer
  html += `<div class="modal-footer">
            <a href="#" class="btn btn-accent" data-dismiss="modal">Close</a>
        </div>`;

  // remove old auto modals
  $('body #auto-modal').remove();

  // remove backdrops from visible modals
  $('body .modal-bg').remove();

  // hide active modals (in case they weren't created by this function)
  $('.modal:visible').modal('hide');

  // add the markup for this modal to the body
  $('body').append(`<div class="modal hide" id="auto-modal">${html}</div>`);

  // return the modal jq object so we can keep using it or chain it
  return $('body #auto-modal').modal();
};

/**
 * @param {JQuery} $element
 * @return {number|null} scrollHeight of the $element
 *  If multiple elements in selection, returns the scrollHeight of the first element.
 */
UI.getScrollHeight = $element => {
  if (!$element.length) {
    return null;
  }
  return $element[0].scrollHeight;
};

onDOMReady(() => {
  if (!isMobile) {
    UI.scrollFixedElements();
  }

  UI.Anchors.init();
});

module.exports = UI;
